import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Keyboard,
  ChevronDown,
  ChevronUp,
  Users,
  GraduationCap,
  AlertCircle,
  CheckCircle,
  Key,
  Copy,
  Plus,
  Trash2,
  Search,
  Check,
} from 'lucide-react';
import {
  createParentUser,
  fetchStudentsForLinking,
  type CreateParentUserDto,
  type StudentForLinking,
  type ParentCredentials,
} from '@/services/userService';

// ==================== CONSTANTS ====================

const ARABIC_KEYBOARD_LAYOUT = [
  ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'],
  ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط'],
  ['ئ', 'ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ', 'د'],
];

const COUNTRY_PHONE_CODES = [
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'US', name: 'États-Unis', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: "Côte d'Ivoire", dialCode: '+225', flag: '🇨🇮' },
];

// Relation options - same values as ParentProfileForm for consistency
// 'value' is stored in UserProfile.relationAvecEtudiant (French)
// 'dbValue' is the Prisma ParentRelationType enum value for StudentParent.relationship (English)
const RELATION_OPTIONS = [
  { value: 'PERE', label: 'Père', dbValue: 'FATHER' },
  { value: 'MERE', label: 'Mère', dbValue: 'MOTHER' },
  { value: 'TUTEUR_LEGAL', label: 'Tuteur légal', dbValue: 'GUARDIAN' },
  { value: 'GRAND_PARENT', label: 'Grand-parent', dbValue: 'GRANDPARENT' },
  { value: 'ONCLE_TANTE', label: 'Oncle / Tante', dbValue: 'OTHER' },
  { value: 'AUTRE', label: 'Autre', dbValue: 'OTHER' },
];

// Relations that require specification
const RELATIONS_REQUIRING_PRECISION = ['TUTEUR_LEGAL', 'AUTRE'];

// Helper to get the database enum value from the French value
const getDbRelationship = (frenchValue: string): StudentLink['relationship'] => {
  const option = RELATION_OPTIONS.find(o => o.value === frenchValue);
  return (option?.dbValue as StudentLink['relationship']) || 'OTHER';
};

// Capitalize first letter of each word (for first names)
const capitalizeFirstLetter = (value: string): string => {
  return value
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// ==================== INTERFACES ====================

interface CreateParentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preSelectedStudentId?: string;
}

interface StudentLink {
  studentId: string;
  relationship: 'FATHER' | 'MOTHER' | 'GUARDIAN' | 'STEPFATHER' | 'STEPMOTHER' | 'GRANDPARENT' | 'SIBLING' | 'OTHER';
  isPrimary: boolean;
}

// ==================== VIRTUAL KEYBOARD ====================

interface VirtualArabicKeyboardProps {
  onKeyPress: (key: string) => void;
  onBackspace: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}

const VirtualArabicKeyboard: React.FC<VirtualArabicKeyboardProps> = ({
  onKeyPress,
  onBackspace,
  onClose,
  anchorRef,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    }
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-xl p-3"
      style={{ top: position.top, left: position.left }}
    >
      <div className="flex justify-between items-center mb-2 pb-2 border-b">
        <span className="text-sm font-medium text-gray-700">Clavier Arabe</span>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>
      <div className="space-y-1" dir="rtl">
        {ARABIC_KEYBOARD_LAYOUT.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-1">
            {row.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => onKeyPress(key)}
                className="w-8 h-8 bg-gray-100 hover:bg-primary-100 rounded text-lg font-arabic transition-colors"
              >
                {key}
              </button>
            ))}
          </div>
        ))}
        <div className="flex justify-center gap-2 mt-2 pt-2 border-t">
          <button
            type="button"
            onClick={() => onKeyPress(' ')}
            className="px-8 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm"
          >
            Espace
          </button>
          <button
            type="button"
            onClick={onBackspace}
            className="px-4 py-1 bg-red-100 hover:bg-red-200 rounded text-sm"
          >
            ← Effacer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ==================== ARABIC INPUT COMPONENT ====================

interface ArabicInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

const ArabicInput: React.FC<ArabicInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  error,
  disabled,
}) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyPress = (key: string) => {
    onChange(value + key);
  };

  const handleBackspace = () => {
    onChange(value.slice(0, -1));
  };

  return (
    <div ref={containerRef} className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-right font-arabic ${
            error ? 'border-red-500' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          dir="rtl"
        />
        <button
          type="button"
          onClick={() => setShowKeyboard(!showKeyboard)}
          disabled={disabled}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600 disabled:opacity-50"
        >
          <Keyboard className="w-5 h-5" />
        </button>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {showKeyboard && !disabled && (
        <VirtualArabicKeyboard
          onKeyPress={handleKeyPress}
          onBackspace={handleBackspace}
          onClose={() => setShowKeyboard(false)}
          anchorRef={containerRef}
        />
      )}
    </div>
  );
};

// ==================== SEARCHABLE STUDENT SELECT ====================

interface SearchableStudentSelectProps {
  students: StudentForLinking[];
  selectedStudentId: string;
  excludedStudentIds: string[];
  onSelect: (studentId: string) => void;
  disabled?: boolean;
}

const SearchableStudentSelect: React.FC<SearchableStudentSelectProps> = ({
  students,
  selectedStudentId,
  excludedStudentIds,
  onSelect,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get available students (not excluded)
  const availableStudents = students.filter(
    s => s.id === selectedStudentId || !excludedStudentIds.includes(s.id)
  );

  // Filter students based on search query
  const filteredStudents = availableStudents.filter(student => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${student.firstName} ${student.lastName}`.toLowerCase();
    const code = student.studentCode?.toLowerCase() || '';
    return fullName.includes(query) || code.includes(query);
  });

  // Get selected student label
  const selectedStudent = students.find(s => s.id === selectedStudentId);
  const displayValue = selectedStudent
    ? `${selectedStudent.firstName} ${selectedStudent.lastName} (${selectedStudent.studentCode})`
    : '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (studentId: string) => {
    onSelect(studentId);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Selected value display / trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-left text-sm ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'
        } ${isOpen ? 'border-primary-500 ring-2 ring-primary-500/20' : 'border-gray-300'}`}
      >
        <span className={selectedStudent ? 'text-gray-900' : 'text-gray-500'}>
          {displayValue || 'Rechercher un étudiant...'}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par nom ou code..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-48 overflow-y-auto">
            {filteredStudents.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                Aucun étudiant trouvé
              </div>
            ) : (
              filteredStudents.map(student => {
                const isSelected = student.id === selectedStudentId;
                return (
                  <button
                    key={student.id}
                    type="button"
                    onClick={() => handleSelect(student.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                    }`}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {student.firstName} {student.lastName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {student.studentCode}
                        {student.filiereInscrit && ` • ${student.filiereInscrit}`}
                      </span>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-primary-600" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

export const CreateParentModal: React.FC<CreateParentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preSelectedStudentId,
}) => {
  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [credentials, setCredentials] = useState<ParentCredentials | null>(null);
  const [copiedField, setCopiedField] = useState<'username' | 'password' | null>(null);

  // Section expansion states
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    contact: true,
    relation: true,
    students: true,
  });

  // Dropdown states
  const [showPhoneDropdown, setShowPhoneDropdown] = useState(false);
  const phoneDropdownRef = useRef<HTMLDivElement>(null);

  // Students data
  const [availableStudents, setAvailableStudents] = useState<StudentForLinking[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // ==================== FORM STATE (same as ParentProfileForm) ====================

  // Identity - French
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  // Identity - Arabic
  const [nomArabe, setNomArabe] = useState('');
  const [prenomArabe, setPrenomArabe] = useState('');
  // Personal info
  const [sexe, setSexe] = useState<'M' | 'F' | ''>('');
  // Contact
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('MA');
  // Address
  const [adresse, setAdresse] = useState('');
  // Relation with student
  const [relationAvecEtudiant, setRelationAvecEtudiant] = useState('');
  const [relationPrecision, setRelationPrecision] = useState('');

  // Student links (additional for staff creation)
  const [studentLinks, setStudentLinks] = useState<StudentLink[]>([]);

  // ==================== EFFECTS ====================

  useEffect(() => {
    if (isOpen) {
      loadStudents();
    }
  }, [isOpen]);

  useEffect(() => {
    // Pre-select student if provided
    if (preSelectedStudentId && availableStudents.length > 0 && studentLinks.length === 0) {
      setStudentLinks([{
        studentId: preSelectedStudentId,
        relationship: 'FATHER',
        isPrimary: true,
      }]);
    }
  }, [preSelectedStudentId, availableStudents]);

  // Close phone dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (phoneDropdownRef.current && !phoneDropdownRef.current.contains(e.target as Node)) {
        setShowPhoneDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ==================== HANDLERS ====================

  const loadStudents = async () => {
    try {
      setLoadingStudents(true);
      const data = await fetchStudentsForLinking();
      setAvailableStudents(data);
    } catch (err) {
      console.error('Error loading students:', err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const resetForm = () => {
    setNom('');
    setPrenom('');
    setNomArabe('');
    setPrenomArabe('');
    setSexe('');
    setEmail('');
    setTelephone('');
    setPhoneCountry('MA');
    setAdresse('');
    setRelationAvecEtudiant('');
    setRelationPrecision('');
    setStudentLinks([]);
    setError(null);
    setSuccess(false);
    setCredentials(null);
    setCopiedField(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFinish = () => {
    onSuccess();
    handleClose();
  };

  const copyToClipboard = async (text: string, field: 'username' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Student link management
  const addStudentLink = () => {
    if (studentLinks.length >= availableStudents.length) return;

    const linkedIds = studentLinks.map(l => l.studentId);
    const availableStudent = availableStudents.find(s => !linkedIds.includes(s.id));

    if (availableStudent) {
      // Use the selected relation type for new students (convert French value to DB enum)
      const relationship = getDbRelationship(relationAvecEtudiant);
      setStudentLinks([
        ...studentLinks,
        {
          studentId: availableStudent.id,
          relationship,
          isPrimary: studentLinks.length === 0,
        },
      ]);
    }
  };

  const removeStudentLink = (index: number) => {
    const newLinks = studentLinks.filter((_, i) => i !== index);
    if (newLinks.length > 0 && !newLinks.some(l => l.isPrimary)) {
      newLinks[0].isPrimary = true;
    }
    setStudentLinks(newLinks);
  };

  const updateStudentLink = (index: number, field: keyof StudentLink, value: string | boolean) => {
    const newLinks = [...studentLinks];
    if (field === 'isPrimary' && value === true) {
      newLinks.forEach((l, i) => {
        l.isPrimary = i === index;
      });
    } else if (field === 'studentId') {
      newLinks[index].studentId = value as string;
    } else if (field === 'relationship') {
      newLinks[index].relationship = value as StudentLink['relationship'];
    }
    setStudentLinks(newLinks);
  };

  // Update all student links when relation type changes (convert French value to DB enum)
  useEffect(() => {
    if (relationAvecEtudiant && studentLinks.length > 0) {
      const dbRelationship = getDbRelationship(relationAvecEtudiant);
      const newLinks = studentLinks.map(link => ({
        ...link,
        relationship: dbRelationship,
      }));
      setStudentLinks(newLinks);
    }
  }, [relationAvecEtudiant]);

  // ==================== VALIDATION & SUBMIT ====================

  const validateForm = (): string | null => {
    // Identity validation
    if (!nom || nom.length < 2) return 'Le nom est requis (min. 2 caractères)';
    if (!prenom || prenom.length < 2) return 'Le prénom est requis (min. 2 caractères)';
    if (!sexe) return 'Veuillez sélectionner le sexe';

    // Contact validation - phone format based on selected country
    if (!telephone) return 'Le numéro de téléphone est requis';
    const cleanedPhone = telephone.replace(/^0+/, '').replace(/\s/g, '');
    if (phoneCountry === 'MA') {
      // Moroccan format: 9 digits starting with 5, 6, or 7
      if (!/^[5-7]\d{8}$/.test(cleanedPhone)) {
        return 'Le numéro doit contenir 9 chiffres commençant par 5, 6 ou 7 (ex: 612345678)';
      }
    } else {
      // International format: must have at least 7 digits
      const digitsOnly = cleanedPhone.replace(/\D/g, '');
      if (digitsOnly.length < 7 || digitsOnly.length > 15) {
        return 'Le numéro de téléphone doit contenir entre 7 et 15 chiffres';
      }
    }
    if (!adresse || adresse.length < 5) return "L'adresse est requise (min. 5 caractères)";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Format d\'email invalide';

    // Relation validation
    if (!relationAvecEtudiant) return 'Veuillez sélectionner la relation avec l\'étudiant';
    if (RELATIONS_REQUIRING_PRECISION.includes(relationAvecEtudiant)) {
      if (!relationPrecision || relationPrecision.length < 2) {
        return 'Veuillez préciser la relation (min. 2 caractères)';
      }
    }

    // Students validation
    if (studentLinks.length === 0) return 'Veuillez sélectionner au moins un étudiant';

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // Build phone number with country code
      const selectedCountry = COUNTRY_PHONE_CODES.find(c => c.code === phoneCountry);
      // Clean the phone number (remove leading zeros and spaces)
      const cleanedPhone = telephone.replace(/^0+/, '').replace(/\s/g, '');
      const fullPhone = `${selectedCountry?.dialCode || '+212'}${cleanedPhone}`;

      const data: CreateParentUserDto = {
        firstName: prenom,
        lastName: nom,
        phone: fullPhone,
        sexe: sexe as string,
        adresse,
        studentLinks: studentLinks.map(link => ({
          studentId: link.studentId,
          relationship: link.relationship,
          isPrimary: link.isPrimary,
        })),
        // Optional fields
        ...(email && { email }),
        ...(nomArabe && { lastNameArabic: nomArabe }),
        ...(prenomArabe && { firstNameArabic: prenomArabe }),
        phoneCountry,
        // Relation with student
        ...(relationAvecEtudiant && { relationAvecEtudiant }),
        ...(relationPrecision && { relationPrecision }),
      };

      const result = await createParentUser(data);
      setSuccess(true);
      setCredentials(result.credentials);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  // ==================== RENDER ====================

  const selectedPhoneCountry = COUNTRY_PHONE_CODES.find((c) => c.code === phoneCountry);
  const showRelationPrecision = RELATIONS_REQUIRING_PRECISION.includes(relationAvecEtudiant);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="text-purple-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Ajouter un parent/tuteur</h2>
              <p className="text-sm text-gray-500">Créer un compte parent et le lier à un ou plusieurs étudiants</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Success Message with Credentials */}
          {success && credentials && (
            <div className="mb-6 space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="text-green-600" size={20} />
                <span className="text-green-800 font-medium">Parent/tuteur créé avec succès !</span>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Key className="text-blue-600" size={20} />
                  <span className="font-medium text-blue-900">Identifiants de connexion</span>
                </div>
                <p className="text-sm text-blue-700 mb-4">
                  Partagez ces identifiants avec le parent. Il devra changer son mot de passe à la première connexion.
                </p>

                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-blue-200">
                    <div>
                      <span className="text-xs text-gray-500 block">Nom d'utilisateur</span>
                      <span className="font-mono font-medium text-gray-900">{credentials.username}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(credentials.username, 'username')}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      {copiedField === 'username' ? <CheckCircle size={18} className="text-green-600" /> : <Copy size={18} />}
                    </button>
                  </div>

                  <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-blue-200">
                    <div>
                      <span className="text-xs text-gray-500 block">Mot de passe temporaire</span>
                      <span className="font-mono font-medium text-gray-900">{credentials.tempPassword}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(credentials.tempPassword, 'password')}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      {copiedField === 'password' ? <CheckCircle size={18} className="text-green-600" /> : <Copy size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* ==================== IDENTITY SECTION ==================== */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('identity')}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-primary-600" />
                    <span className="font-semibold text-gray-900">Identité</span>
                  </div>
                  {expandedSections.identity ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                </button>

                {expandedSections.identity && (
                  <div className="p-6 space-y-6">
                    {/* French Name */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={nom}
                          onChange={(e) => setNom(e.target.value.toUpperCase())}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                          placeholder="Nom de famille"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Prénom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={prenom}
                          onChange={(e) => setPrenom(capitalizeFirstLetter(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Prénom"
                        />
                      </div>
                    </div>

                    {/* Arabic Name */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ArabicInput
                        label="الاسم العائلي (Nom en arabe)"
                        value={nomArabe}
                        onChange={setNomArabe}
                        placeholder="اكتب اسمك العائلي"
                      />
                      <ArabicInput
                        label="الاسم الشخصي (Prénom en arabe)"
                        value={prenomArabe}
                        onChange={setPrenomArabe}
                        placeholder="اكتب اسمك الشخصي"
                      />
                    </div>

                    {/* Sexe */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Sexe <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-4">
                        <label
                          className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                            sexe === 'M' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <input
                            type="radio"
                            value="M"
                            checked={sexe === 'M'}
                            onChange={() => setSexe('M')}
                            className="sr-only"
                          />
                          <span>Masculin</span>
                        </label>
                        <label
                          className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                            sexe === 'F' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <input
                            type="radio"
                            value="F"
                            checked={sexe === 'F'}
                            onChange={() => setSexe('F')}
                            className="sr-only"
                          />
                          <span>Féminin</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ==================== CONTACT SECTION ==================== */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('contact')}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-primary-600" />
                    <span className="font-semibold text-gray-900">Coordonnées</span>
                  </div>
                  {expandedSections.contact ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                </button>

                {expandedSections.contact && (
                  <div className="p-6 space-y-4">
                    {/* Email */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        <Mail className="inline w-4 h-4 mr-1" />
                        Email <span className="text-gray-400 text-xs">(optionnel)</span>
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="email@exemple.com"
                      />
                      <p className="text-xs text-gray-500">
                        Un nom d'utilisateur sera généré automatiquement pour la connexion
                      </p>
                    </div>

                    {/* Phone */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        <Phone className="inline w-4 h-4 mr-1" />
                        Téléphone <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <div className="relative" ref={phoneDropdownRef}>
                          <button
                            type="button"
                            onClick={() => setShowPhoneDropdown(!showPhoneDropdown)}
                            className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 min-w-[100px]"
                          >
                            <span>{selectedPhoneCountry?.flag}</span>
                            <span className="text-sm">{selectedPhoneCountry?.dialCode}</span>
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          </button>
                          {showPhoneDropdown && (
                            <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto min-w-[180px]">
                              {COUNTRY_PHONE_CODES.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  onClick={() => {
                                    setPhoneCountry(country.code);
                                    setShowPhoneDropdown(false);
                                  }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left ${
                                    phoneCountry === country.code ? 'bg-primary-50' : ''
                                  }`}
                                >
                                  <span>{country.flag}</span>
                                  <span className="text-sm">{country.name}</span>
                                  <span className="text-xs text-gray-500 ml-auto">{country.dialCode}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <input
                          type="tel"
                          value={telephone}
                          onChange={(e) => setTelephone(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="6 12 34 56 78"
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        <MapPin className="inline w-4 h-4 mr-1" />
                        Adresse <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={adresse}
                        onChange={(e) => setAdresse(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Adresse complète"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ==================== RELATION SECTION ==================== */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('relation')}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary-600" />
                    <span className="font-semibold text-gray-900">Relation avec l'étudiant</span>
                  </div>
                  {expandedSections.relation ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                </button>

                {expandedSections.relation && (
                  <div className="p-6 space-y-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Type de relation <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={relationAvecEtudiant}
                        onChange={(e) => setRelationAvecEtudiant(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Sélectionner...</option>
                        {RELATION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {showRelationPrecision && (
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Précisez la relation <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={relationPrecision}
                          onChange={(e) => setRelationPrecision(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder={
                            relationAvecEtudiant === 'GUARDIAN'
                              ? "Ex: Tuteur désigné par le tribunal, Famille d'accueil..."
                              : "Précisez votre relation avec l'étudiant"
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ==================== STUDENTS SECTION ==================== */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('students')}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-primary-600" />
                    <span className="font-semibold text-gray-900">Étudiants associés</span>
                    <span className="text-xs text-red-500">*Au moins un</span>
                    {studentLinks.length > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                        {studentLinks.length}
                      </span>
                    )}
                  </div>
                  {expandedSections.students ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                </button>

                {expandedSections.students && (
                  <div className="p-6 space-y-4">
                    {loadingStudents ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent"></div>
                      </div>
                    ) : availableStudents.length === 0 ? (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-yellow-800 text-sm">
                          Aucun étudiant disponible. Créez d'abord des étudiants avant de créer des parents.
                        </p>
                      </div>
                    ) : (
                      <>
                        {studentLinks.map((link, index) => (
                          <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700">Étudiant {index + 1}</span>
                              <button
                                type="button"
                                onClick={() => removeStudentLink(index)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>

                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Sélectionner l'étudiant</label>
                                <SearchableStudentSelect
                                  students={availableStudents}
                                  selectedStudentId={link.studentId}
                                  excludedStudentIds={studentLinks.filter((_, i) => i !== index).map(l => l.studentId)}
                                  onSelect={(studentId) => updateStudentLink(index, 'studentId', studentId)}
                                />
                              </div>

                              <div className="flex items-center">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={link.isPrimary}
                                    onChange={(e) => updateStudentLink(index, 'isPrimary', e.target.checked)}
                                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                                  />
                                  <span className="text-sm text-gray-700">Contact principal pour cet étudiant</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}

                        {studentLinks.length < availableStudents.length && (
                          <button
                            type="button"
                            onClick={addStudentLink}
                            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors"
                          >
                            <Plus size={18} />
                            <span>Ajouter un étudiant</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          {success && credentials ? (
            <button
              type="button"
              onClick={handleFinish}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
            >
              <CheckCircle size={18} />
              Terminer
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={loading || availableStudents.length === 0}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Création...
                  </>
                ) : (
                  'Créer le parent'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateParentModal;
